import { DateTime } from 'luxon';
import store from '@state/store';

/**
 * Get the browser's timezone
 */
export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('Error detecting browser timezone:', error);
    return 'UTC';
  }
}

/**
 * Validate if a timezone string is valid
 */
export function isValidTimezone(timezone) {
  try {
    DateTime.now().setZone(timezone);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get the user's timezone from the store
 * Falls back to browser timezone if not set or invalid
 */
export function getUserTimezone() {
  const userTimezone = store.state?.auth?.currentUser?.timezone;
  
  if (userTimezone) {
    // Validate timezone to prevent crashes
    if (isValidTimezone(userTimezone)) {
      return userTimezone;
    }
    console.error('Invalid timezone in database:', userTimezone);
  }
  
  // Fallback to browser timezone
  return getBrowserTimezone();
}

/**
 * Normalize event from API for FullCalendar: start/end as UTC ISO strings
 * @param {Object} event - Event from API
 * @returns {Object} Normalized event; display times come from start/end via toUserZone
 */
export function normalizeEventFromAPI(event) {
  if (!event.start || !event.end) {
    console.warn('Event has no start/end', event);
    return event;
  }

  // Could be UTC, carry an offset (Google Calendar), or be date-only for all-day events
  const isAllDay = event.allDay || !event.start.includes('T');

  return {
    ...event,
    start: DateTime.fromISO(event.start).toUTC().toISO(),
    end: DateTime.fromISO(event.end).toUTC().toISO(),
    allDay: isAllDay
  };
}

/**
 * An instant from the API (UTC ISO string) as a DateTime in the user's timezone
 */
export function toUserZone(iso) {
  return DateTime.fromISO(iso, { zone: 'UTC' }).setZone(getUserTimezone());
}

// Every event time on screen goes through these, in the user's timezone
export function formatEventTime(iso) {
  return toUserZone(iso).toFormat('HH:mm');
}

export function formatEventHours(event) {
  return `${formatEventTime(event.start)} - ${formatEventTime(event.end)}`;
}

export function formatEventDate(iso) {
  return toUserZone(iso).toFormat('dd/MM/yyyy');
}

// Today's date (yyyy-MM-dd) in the user's timezone, the day the API's `date` filters mean
export function userToday() {
  return DateTime.now().setZone(getUserTimezone()).toISODate();
}

/**
 * A date picker value for the user-timezone day of a DateTime.
 * The picker reads its Date in the browser's timezone, so it gets that calendar day there.
 */
export function toPickerDate(dateTime) {
  const local = dateTime.setZone(getUserTimezone());
  return DateTime.fromObject({ year: local.year, month: local.month, day: local.day }).toJSDate();
}

// First and last day (inclusive) of the user-timezone month `offset` months from now, as picker values
export function monthPickerRange(offset = 0) {
  const month = DateTime.now().setZone(getUserTimezone()).startOf('month').plus({ months: offset });
  return [toPickerDate(month), toPickerDate(month.endOf('month'))];
}

// The yyyy-MM-dd day a date picker value shows
export function pickerDay(date) {
  return DateTime.fromJSDate(date).toISODate();
}

/**
 * The event modal's fields for a start/end: the date picker's Date and HH:mm strings, in the user's timezone
 * @param {DateTime} start
 * @param {DateTime} end
 */
export function toEventForm(start, end) {
  const timezone = getUserTimezone();
  const startLocal = start.setZone(timezone);
  const endLocal = end.setZone(timezone);
  return {
    formDate: toPickerDate(startLocal),
    formStartTime: startLocal.toFormat('HH:mm'),
    formEndTime: endLocal.toFormat('HH:mm'),
    formDuration: Math.round(endLocal.diff(startLocal, 'minutes').minutes)
  };
}

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// A form time the API payload can be built from: exactly HH:mm, 00:00-23:59
export function isValidTime(value) {
  return typeof value === 'string' && TIME_PATTERN.test(value);
}

// Completes partial time input on blur: 9 -> 09:00, 22 or 22: -> 22:00, 22:3 -> 22:30.
// Anything else is returned as typed, for validation to reject.
export function normalizeTimeInput(value) {
  const match = /^(\d{1,2})(?::(\d?))?$/.exec(typeof value === 'string' ? value.trim() : '');
  if (!match) return value;
  const [, hours, minutes] = match;
  const time = `${hours.padStart(2, '0')}:${minutes ? `${minutes}0` : '00'}`;
  return isValidTime(time) ? time : value;
}

// Thrown instead of building a payload with a missing or unparseable start/end
export class InvalidEventTimeError extends Error {}

const toPayloadRange = (start, end) => {
  if (!start.isValid || !end.isValid) throw new InvalidEventTimeError('Invalid event start or end');
  return { start: start.toUTC().toISO(), end: end.toUTC().toISO() };
};

/**
 * Convert ISO strings from FullCalendar drag/drop/resize to UTC for API
 * @param {string} startISO - ISO string from FullCalendar (e.g., "2024-01-15T14:00:00+02:00")
 * @param {string} endISO - ISO string from FullCalendar
 * @returns {Object} { start, end } as UTC ISO strings
 * @throws {InvalidEventTimeError} when either string is missing or unparseable, or end isn't after start
 */
export function convertCalendarEventToUTC(startISO, endISO) {
  const start = DateTime.fromISO(startISO || '');
  const end = DateTime.fromISO(endISO || '');
  if (start.isValid && end.isValid && end <= start) {
    throw new InvalidEventTimeError('Event must end after it starts');
  }
  return toPayloadRange(start, end);
}

// The yyyy-MM-dd the form's date stands for, or null
const formDay = (formDate) => {
  if (formDate instanceof Date) return DateTime.fromJSDate(formDate).toISODate();
  if (DateTime.isDateTime(formDate)) return formDate.toISODate();
  if (typeof formDate === 'string') return DateTime.fromISO(formDate).toISODate();
  return null;
};

/**
 * Turn the event form (picker date + HH:mm times, user's timezone) into the API payload
 * @param {Object} eventData - form fields formDate/formStartTime/formEndTime plus the rest of the event
 * @returns {Object} the rest of the event with start/end in UTC
 * @throws {InvalidEventTimeError} when the date or either time isn't valid
 */
export function prepareEventForAPI(eventData) {
  // duration is never sent: the API reads it from start/end
  const { formDate, formStartTime, formEndTime, duration, ...rest } = eventData;
  const timezone = getUserTimezone();
  const day = formDay(formDate);

  if (!day || !isValidTime(formStartTime) || !isValidTime(formEndTime)) {
    throw new InvalidEventTimeError('Invalid event date or time');
  }

  const start = DateTime.fromFormat(`${day} ${formStartTime}`, 'yyyy-MM-dd HH:mm', { zone: timezone });
  let end = DateTime.fromFormat(`${day} ${formEndTime}`, 'yyyy-MM-dd HH:mm', { zone: timezone });
  // An end at or before the start (e.g. 23:00 -> 01:00) is on the next day
  if (start.isValid && end.isValid && end <= start) end = end.plus({ days: 1 });

  return { ...rest, ...toPayloadRange(start, end), timezone };
}

const sameMinute = (a, b) =>
  !!a && !!b && DateTime.fromISO(a).startOf('minute').equals(DateTime.fromISO(b).startOf('minute'));

/**
 * An edit's payload without start/end when they're still the times the event was loaded with.
 * The event may have moved since (in Google, or another tab), and re-sending what the form
 * loaded would move it back. Compared to the minute, as the form shows.
 * @param {Object} payload - from prepareEventForAPI
 * @param {Object} loaded - the event as the form loaded it
 */
export function omitUnchangedTimes(payload, loaded) {
  if (!sameMinute(payload.start, loaded?.start) || !sameMinute(payload.end, loaded?.end)) return payload;
  const { start, end, ...rest } = payload;
  return rest;
}

